<template>
	<BaseLayout
		:footer-full="showFullFooter"
		:include-padding="false"
		:transparent-header="false"
		:hide-header="isHeaderHidden"
	>
		<template v-if="props.isInvitationProgram" #header>
			<HeaderBasic :set-border="false" classes="pt-150" />
		</template>
		<template v-if="showHomepageFooter" #footer>
			<FooterHomePage v-bind="footerHomePageProps" />
		</template>
		<template v-else-if="props.isInvitationProgram" #footer>
			<FooterFull>
				<template #additionalDisclaimer>
					<FooterSocialProof />
				</template>
			</FooterFull>
		</template>
		<BasePlaceHolder :line-count="10" :class="{ 'container pt-300 pb-300': isLoading }" :placehold="isLoading">
			<section v-for="(component, componentId) in pageComponents" :key="componentId">
				<component :is="component.component" v-bind="component.props">
					<template v-if="displayNotice" #hero-disclaimer>
						<div class="container pb-200">
							<SolicitorNotice
								:solicitor="solicitorName"
								:payment="solicitorPayment"
								:is-cpa-partner="solicitorIsCpaPartner"
								:is-investor="solicitorIsInvestor"
								:monthly-payment="solicitorMonthlyPayment"
								:custom-payment-condition="solicitorCustomCondition"
								:text-dark="!isHeroDarkTheme"
							/>
						</div>
					</template>
				</component>
			</section>
		</BasePlaceHolder>
		<div
			v-if="isLoading"
			class="container display-flex bt marketing-section justify-content-space-between flex-wrap-wrap gap-100-smo"
		>
			<BasePlaceHolder :line-count="8" :placehold="isLoading" class="col-12 col-7-md" />
			<BasePlaceHolder :line-count="8" :placehold="isLoading" class="col-12 col-4-md" />
		</div>
	</BaseLayout>
</template>

<script lang="ts" setup>
import { computed, markRaw, onUnmounted, provide, ref } from 'vue';
import { MediaPartnerNoticeData, SanityInfoCardsSection } from 'types/sanity/marketing-components';
import { SanityLandingHero, SanityModularLandingPageData } from 'types/sanity/modular-landing-page';
import { useRoute, useRouter } from 'vue-router';
import { appCloudfrontPath } from '@constants';
import { AxiosError } from 'axios';
import { CallToAction } from 'types/layout';
import CtaBanner from '@components/layout/cta-banner.vue';
import { currency } from '@filters/shared-filters';
import { defaultSolicitorNotice } from '@constants/marketing-site';
import { DynamicComponent } from 'types/vue';
import FooterFull from '@components/footer/footer-full.vue';
import FooterHomePage from '@components/footer/footer-home-page.vue';
import FooterSocialProof from '@components/footer/footer-social-proof.vue';
import { getModularLandingPage } from '@api/sanity/graphql/modular-landing-page';
import { getReferralByKey } from '@api/referral';
import HeaderBasic from '@components/header/header-basic.vue';
import HomepageContent from '@components/marketing-site/homepage-content.vue';
import HomepageContentRange from '@components/marketing-site/homepage-content-range.vue';
import HomepageHero from '@components/marketing-site/homepage-hero.vue';
import { isValidSlug } from '@utils/routing';
import LandingHero from '@components/marketing-site/landing-page/landing-hero.vue';
import landingInfoCardPanelV2 from '@components/marketing-site/landing-page/landing-info-card-panel.vue';
import LandingInfoCards from '@components/marketing-site/landing-page/landing-info-cards.vue';
import LandingRecommendedArticles from '@components/marketing-site/landing-page/landing-recommended-articles.vue';
import LandingToutsSection from '@components/marketing-site/landing-page/landing-touts-section.vue';
import { PromoInvestmentType } from 'types/promos';
import ReferralWelcomeHero from '@components/referral/referral-welcome-hero.vue';
import { ReferrerUser } from 'types/referral';
import { SanityRelatedArticlesSection } from 'types/sanity/blog-education-center';
import SolicitorNotice from '@components/marketing-site/solicitor-notice.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';

interface Props {
	partnerNotice?: MediaPartnerNoticeData;
	campaignName?: string;
	isAcqPlusStart?: boolean;
	isInvitationProgram?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	partnerNotice: undefined,
	campaignName: undefined,
	isAcqPlusStart: false,
	isInvitationProgram: false
});

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const isLoading = ref(true);

provide('showInvestopedia', ref(false));
provide('showNerdwallet', ref(false));

const isRedirect = !!route.redirectedFrom;
const redirectLocation = `${window.location.origin}${route.fullPath}`;
const redirectMeta = isRedirect
	? [
			{
				name: 'prerender-status-code',
				content: '301'
			},
			{
				name: 'prerender-header',
				content: `Location: ${redirectLocation}`
			}
		]
	: [];

const page = ref<SanityModularLandingPageData | null>(null);
const pageComponents = ref<Array<DynamicComponent>>([]);

const showHomepageFooter = ref(false);
const footerHomePageProps = ref<{ showFeeWaiverDisclaimer: boolean }>({
	showFeeWaiverDisclaimer: true
});

const referrerUser = ref<ReferrerUser | null>(null);
const referredUserFeeName = ref('');
const leadCaptureReferralKey = ref('');
const isHeroDarkTheme = ref(false);

const displayNotice = ref(false);
const solicitorName = ref(defaultSolicitorNotice.name);
const solicitorPayment = ref(defaultSolicitorNotice.payment);
const solicitorMonthlyPayment = ref(defaultSolicitorNotice.monthlyPayment);
const solicitorIsCpaPartner = ref(defaultSolicitorNotice.isCpaPartner);
const solicitorIsInvestor = ref(defaultSolicitorNotice.isInvestor);
const solicitorCustomCondition = ref(defaultSolicitorNotice.customCondition);

const showFullFooter = computed((): boolean => {
	return !!page.value?.showFullFooter;
});

const showMainNav = computed((): boolean => {
	return !!page.value?.showMainNav;
});

const isHeaderHidden = computed((): boolean => {
	return !showMainNav.value && !props.isInvitationProgram;
});

const promoInvestmentType = computed((): PromoInvestmentType | undefined => {
	if (props.isInvitationProgram && leadCaptureReferralKey.value) {
		return 'INVITATION_PROGRAM_INVITEE';
	}
	return undefined;
});

async function setupPageComponents(): Promise<void> {
	for (const component of page.value?.components ?? []) {
		switch (component._type) {
			case 'landingPageHero': {
				await buildLandingHeroComponent(component);
				break;
			}
			case 'ctaPanel': {
				const ctaComponent: CallToAction = {
					heading: component.ctaHeading,
					summary: component.ctaSummary,
					link: {
						text: component.ctaLink.linkText ?? 'Get started',
						href: component.ctaLink.url ?? '/get-started',
						target: component.ctaLink.openInNewTab ? '_blank' : '_self'
					}
				};
				pageComponents.value.push({
					component: markRaw(CtaBanner),
					props: {
						cta: ctaComponent
					}
				});
				break;
			}
			case 'reusableContent': {
				if (component.type === 'homepageContent') {
					showHomepageFooter.value = true;
					pageComponents.value.push({
						component: markRaw(HomepageContent)
					});
				} else if (component.type === 'homepageContentRange') {
					showHomepageFooter.value = true;
					footerHomePageProps.value = {
						showFeeWaiverDisclaimer: false
					};
					pageComponents.value.push({
						component: markRaw(HomepageContentRange)
					});
				}
				break;
			}
			case 'relatedArticles': {
				const relatedArticlesSection: SanityRelatedArticlesSection = component;
				if (relatedArticlesSection.relatedArticles?.length) {
					pageComponents.value.push({
						component: markRaw(LandingRecommendedArticles),
						props: {
							relatedArticlesSection
						}
					});
				}

				break;
			}
			case 'infoCardsSection': {
				const infoCardsData: SanityInfoCardsSection = {
					isSectionDarkTheme: component.isSectionDarkTheme,
					panelOverline: component.panelOverline,
					panelHeading: component.panelHeading,
					sectionSummary: component.sectionSummary,
					sectionCards: component.sectionCards
				};
				pageComponents.value.push({
					component: markRaw(LandingInfoCards),
					props: {
						data: infoCardsData
					}
				});
				break;
			}
			case 'infoCard': {
				pageComponents.value.push({
					component: markRaw(landingInfoCardPanelV2),
					props: {
						card: component
					}
				});
				break;
			}
			case 'toutsPanel': {
				pageComponents.value.push({
					component: markRaw(LandingToutsSection),
					props: {
						data: component
					}
				});
			}
		}
	}
}

async function setupInvitationProgramState(): Promise<void> {
	try {
		const inviteKey = route.query.inviteKey;
		if (typeof inviteKey !== 'string' || !inviteKey || !isValidSlug(inviteKey)) {
			await router.push({
				name: '404'
			});
			return;
		}

		referrerUser.value = await getReferralByKey(inviteKey);
		leadCaptureReferralKey.value = inviteKey;
	} catch (error: unknown) {
		const e = error as AxiosError;
		if (e.response?.status === 400) {
			appStore.addToast({
				type: 'error',
				message: 'Invalid invite link'
			});
		}
		await router.push({
			name: 'home'
		});
	}
}

async function buildLandingHeroComponent(hero: SanityLandingHero): Promise<void> {
	let componentDef: DynamicComponent;

	switch (hero.stub) {
		case 'HomepageHero':
			componentDef = {
				component: HomepageHero,
				props: {
					leadCaptureFeeWaiver: referredUserFeeName.value,
					showBrandLogos: !page.value?.showMainNav,
					showLeadCapture: true
				}
			};
			break;
		case 'ReferralHero':
			componentDef = {
				component: ReferralWelcomeHero,
				props: {
					leadCaptureReferralKey: leadCaptureReferralKey.value,
					referrerUser: referrerUser.value
				}
			};
			break;
		default:
			if (hero.feeWaiverKey) {
				referredUserFeeName.value = hero.feeWaiverKey;
			}

			if (hero.includeSolicitorNotice) {
				displayNotice.value = true;
				solicitorName.value = hero.solicitorName ?? solicitorName.value;
				solicitorPayment.value = hero.maxCPAPayment ?? currency(50, 0);
				solicitorMonthlyPayment.value = hero.monthlyPayment ?? solicitorMonthlyPayment.value;
				solicitorIsInvestor.value = hero.isInvestor ?? false;
				solicitorIsCpaPartner.value = hero.isCPAPartner ?? false;
			}

			componentDef = {
				component: LandingHero,
				props: {
					hero,
					leadCaptureFeeWaiver: referredUserFeeName.value,
					leadCaptureReferralKey: leadCaptureReferralKey.value,
					promoInvestmentType: promoInvestmentType.value,
					showBrandLogos: !page.value?.showMainNav
				}
			};
			isHeroDarkTheme.value = hero.isDarkTheme;
	}

	pageComponents.value.push({
		component: markRaw(componentDef.component),
		props: componentDef.props
	});
}

function setupPartnerNoticeState(partnerNotice: MediaPartnerNoticeData): void {
	displayNotice.value = true;
	solicitorName.value = partnerNotice.name ?? solicitorName.value;
	solicitorPayment.value = partnerNotice.payment ?? solicitorPayment.value;
	solicitorIsCpaPartner.value = partnerNotice.isCpaPartner;
	solicitorIsInvestor.value = partnerNotice.isInvestor ?? false;
	solicitorMonthlyPayment.value = partnerNotice.monthlyPayment ?? solicitorMonthlyPayment.value;
	solicitorCustomCondition.value = partnerNotice.customPaymentCondition ?? solicitorCustomCondition.value;
}

const headControl = useHead({
	meta: redirectMeta,
	script: [`window.prerenderReady = false;`]
});

onUnmounted(() => {
	headControl?.dispose();
});

async function setup(): Promise<void> {
	try {
		const campaignName = props.campaignName as string;
		const pageData = await getModularLandingPage(campaignName);

		if (!pageData) {
			await router.push({ path: '/404' });
		}

		page.value = pageData;
		const pageTitle = page.value?.pageTitle ?? undefined;
		const inviteDescription =
			'You have been invited to join the 2+ million people using Fundrise to start investing in private assets.';

		const meta = [
			...(props.isInvitationProgram
				? [
						{
							name: 'description',
							content: inviteDescription
						},
						{
							property: 'og:description',
							content: inviteDescription
						},
						{ property: 'og:title', content: 'Join me on Fundrise, get up to $100 in shares' },
						{
							property: 'og:image',
							content: `${appCloudfrontPath}/images/marketing-site/invite_share_promo.png`
						},
						{ name: 'robots', content: 'noindex' }
					]
				: [
						{ property: 'og:title', content: pageTitle },
						{ name: 'description', content: page.value?.pageSummary },
						{ property: 'og:description', content: page.value?.pageSummary }
					]),
			...redirectMeta
		];
		headControl?.patch({
			title: pageTitle,
			meta,
			script: [`window.prerenderReady = true;`]
		});

		if (props.isInvitationProgram) {
			await setupInvitationProgramState();
		}

		if (page.value?.components?.length) {
			await setupPageComponents();
		}

		if (props.partnerNotice) {
			setupPartnerNoticeState(props.partnerNotice);
		}
	} catch {
		await router.push({ path: '/404' });
	} finally {
		isLoading.value = false;
	}
}

setup();
</script>
